import { useGetMyCurrentBalance } from '@/api/balance/balance';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useViewMode } from '@/contexts/ViewModeContext';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AdminNavigationLinks } from './AdminNavigationLinks';
import { NavigationLinks } from './NavigationLinks';

export const HeaderNavigation = () => {
  const { user, isAuthenticated, logout, isAdmin, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { viewMode, toggleAdminView } = useViewMode();

  // Get current user balance
  const { data: userBalance } = useGetMyCurrentBalance({
    query: {
      enabled: isAuthenticated,
    },
  });

  const handleLogout = () => {
    logout().then(() => {
      navigate('/');
    });
  };

  const handleMobileLogout = () => {
    setIsMobileMenuOpen(false);
    handleLogout();
  };

  return (
    <header className='flex px-6 py-3 bg-white shadow-sm'>
      {/* Central div, max width is 1280px */}
      <div className='max-w-7xl w-full mx-auto flex items-center justify-between'>
        {/* Left: Logo + Nav */}
        <div className='flex items-center space-x-8'>
          {/* Logo */}
          <Link to='/' className='flex items-center space-x-1'>
            <img src='/RZ_SWD_Logo_Redesign_Vertikal_RGB_positiv 1.png' alt='Logo' className='w-20 h-auto' />
          </Link>

          {/* Nav Links - Hidden on mobile */}
          <div className='hidden md:block'>
            {isAuthenticated && isAdmin && viewMode === 'admin' ? (
              <AdminNavigationLinks />
            ) : (
              <NavigationLinks isAuthenticated={isAuthenticated} />
            )}
          </div>
        </div>

        {/* Right: Mobile Menu + Search + Icons */}
        <div className='flex items-center gap-4'>
          {isAuthenticated && (
            <>
              {/* Balance Display */}
              {userBalance && (
                <div className='px-2 py-0.5 rounded-xl outline-1 outline-offset-[-1px] outline-swd-tuerkis-dark inline-flex justify-start items-start gap-2.5 text-swd-tuerkis-dark'>
                  <div className='ext-swd-tuerkis-dark text-sm font-bold'>
                    Guthaben: {userBalance.remaining_credit.toFixed(0)} €
                  </div>
                </div>
              )}

              {/* Avatar */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' className='relative px-0 w-10 h-8 rounded-full'>
                    <Avatar className='h-10 w-10'>
                      <AvatarFallback>{user?.name?.charAt(0).toUpperCase() || 'U'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-56' align='end' forceMount>
                  <DropdownMenuLabel className='font-normal'>
                    <div className='flex flex-col space-y-1'>
                      <p className='text-sm font-medium leading-none'>{user?.name}</p>
                      <p className='text-xs leading-none text-muted-foreground'>{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <DropdownMenuCheckboxItem
                      checked={viewMode === 'admin'}
                      onCheckedChange={() => {
                        const nextIsAdmin = viewMode !== 'admin';
                        toggleAdminView();
                        if (nextIsAdmin) {
                          navigate('/admin');
                        } else if (location.pathname.startsWith('/admin')) {
                          navigate('/');
                        }
                      }}
                    >
                      Admin-Ansicht
                    </DropdownMenuCheckboxItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/balance/history')}>Guthaben-Verlauf</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout}>Abmelden</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Mobile Menu Button - Only visible on mobile */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant='ghost' size='icon' className='md:hidden w-10 h-8'>
                <Menu className='h-5 w-5' />
                <span className='sr-only'>Menü öffnen</span>
              </Button>
            </SheetTrigger>
            <SheetContent side='left' className='w-80 p-0'>
              <div className='flex flex-col h-full'>
                {/* Logo Section */}
                <div className='p-6 border-b'>
                  <Link to='/' className='flex items-center space-x-1' onClick={() => setIsMobileMenuOpen(false)}>
                    <img src='/RZ_SWD_Logo_Redesign_Vertikal_RGB_positiv 1.png' alt='Logo' className='w-16 h-auto' />
                  </Link>
                </div>

                {/* Navigation Links */}
                <div className='flex-1 py-6'>
                  <nav className='flex flex-col space-y-1 px-6'>
                    {isAuthenticated ? (
                      isAdmin && viewMode === 'admin' ? (
                        <>
                          <Link
                            to='/admin'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Übersicht
                          </Link>
                          <Link
                            to='/admin/vouchers'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Gutscheine
                          </Link>
                          <Link
                            to='/admin/providers'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Partner
                          </Link>
                          <Link
                            to='/admin/balance'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Budget
                          </Link>
                          <Link
                            to='/admin/users'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Nutzer
                          </Link>
                          <Link
                            to='/admin/access-control'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Zugriffskontrolle
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link
                            to='/coupons'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Gutscheine entdecken
                          </Link>
                          <Link
                            to='/meine-gutscheine'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Meine Gutscheine
                          </Link>
                          <Link
                            to='/balance/history'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Guthaben-Verlauf
                          </Link>
                          <Link
                            to='/so-funktioniert-es'
                            className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            So funktioniert's
                          </Link>
                        </>
                      )
                    ) : (
                      <Link
                        to='/so-funktioniert-es'
                        className='text-gray-600 hover:text-cyan-700 py-3 text-base border-b border-gray-100'
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        So funktioniert's
                      </Link>
                    )}
                  </nav>
                </div>

                {/* User Section */}
                {isAuthenticated ? (
                  <div className='border-t p-6'>
                    <div className='flex items-center space-x-3 mb-4'>
                      <Avatar className='h-10 w-10'>
                        <AvatarFallback>{user?.name?.charAt(0).toUpperCase() || 'U'}</AvatarFallback>
                      </Avatar>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-gray-900 truncate'>{user?.name}</p>
                        <p className='text-xs text-gray-500 truncate'>{user?.email}</p>
                      </div>
                    </div>
                    {isAdmin && (
                      <Button
                        variant='outline'
                        className='w-full mb-2'
                        onClick={() => {
                          const nextIsAdmin = viewMode !== 'admin';
                          toggleAdminView();
                          if (nextIsAdmin) {
                            navigate('/admin');
                          } else if (location.pathname.startsWith('/admin')) {
                            navigate('/');
                          }
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {viewMode === 'admin' ? 'Zur Kundenansicht wechseln' : 'Zur Admin-Ansicht wechseln'}
                      </Button>
                    )}
                    <Button variant='outline' className='w-full' onClick={handleMobileLogout}>
                      Abmelden
                    </Button>
                  </div>
                ) : (
                  <div className='border-t p-6'>
                    <Button
                      className='w-full'
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        login();
                      }}
                    >
                      Anmelden
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {!isAuthenticated && <Button onClick={login}>Anmelden</Button>}
        </div>
      </div>
    </header>
  );
};
